<template>
  <div>
    <v-dialog
      :value="true"
      @input="$router.back()"
      max-width="900px"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      scrollable
      :persistent="isLoading || isFetchingEmployees">
      <v-card class="employee-dialog-card">
        <v-card-title class="text-h5 justify-center pt-6 pb-4">
          Mitarbeiter auswählen
          <v-btn icon absolute top right @click="$router.back()" :disabled="isLoading || isFetchingEmployees">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-subtitle class="text-center pb-6"> Wähle aus, in wessen Namen du arbeitest. </v-card-subtitle>

        <v-card-text>
          <v-container fluid>
            <v-row v-if="isFetchingEmployees" dense>
              <v-col v-for="n in 6" :key="n" cols="12" sm="6" md="4">
                <div class="employee-card employee-card--disabled">
                  <div class="employee-avatar-wrapper">
                    <v-skeleton-loader class="employee-avatar" type="avatar"></v-skeleton-loader>
                  </div>
                  <div class="employee-card-content text-center">
                    <v-skeleton-loader type="text" width="80%" class="mx-auto mb-3"></v-skeleton-loader>
                    <v-skeleton-loader type="text" width="60%" class="mx-auto"></v-skeleton-loader>
                  </div>
                </div>
              </v-col>
            </v-row>

            <v-row v-else dense>
              <v-col v-for="employee in employees" :key="employee.user" cols="12" sm="6" md="4">
                <div
                  class="employee-card"
                  :class="{
                    'employee-card--selected': selected && selected.user === employee.user,
                    'employee-card--disabled': isLoading,
                  }"
                  @click="handleEmployeeSelect(employee)">
                  <div class="employee-avatar-wrapper">
                    <v-avatar class="employee-avatar" size="100">
                      <v-img :src="'/img/profilbilder/' + employee.picture" :lazy-src="'/img/icons/user_placeholder.png'" alt="Profilbild">
                        <template v-slot:placeholder>
                          <v-row class="fill-height ma-0" align="center" justify="center">
                            <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
                          </v-row>
                        </template>
                      </v-img>
                    </v-avatar>
                  </div>

                  <div class="employee-card-content text-center">
                    <div class="employee-name">{{ employee.fullName }}</div>
                    <div class="employee-title">{{ employee.title }}</div>
                  </div>

                  <div v-if="selected && selected.user === employee.user" class="selected-checkmark">
                    <v-icon color="white">mdi-check-decagram</v-icon>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-btn text large @click="clearSelection" :disabled="isLoading || isFetchingEmployees"> Auswahl aufheben </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="isLoading" hide-overlay persistent width="300" content-class="elevation-0">
      <v-card class="pt-4 glass-card">
        <v-card-text class="text-center">
          <v-avatar class="mb-4" size="80" style="border: 3px solid rgba(255, 255, 255, 0.5)">
            <v-img :src="loadingEmployee.picture ? '/img/profilbilder/' + loadingEmployee.picture : '/img/icons/user_placeholder.png'"></v-img>
          </v-avatar>
          <h3 class="mb-3" style="color: #1a2333">Wird gewechselt...</h3>
          <v-progress-linear indeterminate color="primary" class="mb-0" rounded height="6"></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex"
import employeeApi from "@/api/employeeApi"

export default {
  name: "EmployeeSelectionDialog",
  data() {
    return {
      isLoading: false,
      loadingEmployee: {},
      employees: [],
      isFetchingEmployees: false, // NEU: Eigener Ladezustand für die Mitarbeiterliste
    }
  },
  computed: {
    ...mapGetters("studioStore", {
      selected: "getSelectedEmployee",
    }),
  },
  async created() {
    // NEU: Ladezustand steuern
    this.isFetchingEmployees = true
    try {
      this.employees = await employeeApi.fetchStudioAccountEmployee()
    } catch (error) {
      console.error("Mitarbeiter konnten nicht geladen werden:", error)
      if (this.$toast) {
        this.$toast.error("Mitarbeiterliste konnte nicht geladen werden.")
      }
    } finally {
      // NEU: Ladezustand beenden, egal was passiert
      this.isFetchingEmployees = false
    }
  },
  methods: {
    ...mapActions("studioStore", ["selectEmployee"]),
    ...mapActions("panelStore", ["selectCustomer", "clearCustomer"]),

    handleEmployeeSelect(employee) {
      if (this.isLoading || this.isFetchingEmployees) return

      if (this.selected && this.selected.user === employee.user) {
        this.$router.back()
        return
      }

      this.loadingEmployee = employee
      this.isLoading = true

      setTimeout(() => {
        this.selectEmployee(employee)
        this.isLoading = false
        this.$router.back()
      }, 1200)
    },
    clearSelection() {
      if (this.isLoading || this.isFetchingEmployees) return
      this.selectEmployee(null)
      this.clearCustomer()
    },
  },
}
</script>

<style scoped>
/* Bestehende Styles bleiben unverändert */
.employee-dialog-card {
  border-radius: 24px !important;
}

.employee-card {
  position: relative;
  background-color: #f7f7f7;
  border-radius: 20px;
  margin-top: 50px;
  padding: 20px;
  padding-top: 65px;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  border: 2px solid transparent;
  min-height: 180px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.employee-card--disabled {
  pointer-events: none;
  opacity: 0.7;
}

.employee-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.08);
}

.employee-card--selected {
  border-color: var(--v-primary, #00b24a);
  background-color: white;
  box-shadow: 0 10px 30px rgba(0, 178, 74, 0.2);
}

.employee-avatar-wrapper {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
}

.employee-avatar {
  border: 5px solid white;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
}

/* NEU: Styling für Skeleton-Loader innerhalb der Avatar-Größe */
.employee-avatar .v-skeleton-loader__avatar {
  height: 100px;
  width: 100px;
}

.employee-name {
  font-weight: 700;
  font-size: 1.1rem;
  color: #2c3e50;
  margin-bottom: 4px;
}

.employee-title {
  font-size: 0.9rem;
  color: #7f8c8d;
}

.selected-checkmark {
  position: absolute;
  top: -10px;
  right: -10px;
  background-color: var(--v-primary, #00b24a);
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  z-index: 3;
}

.glass-card {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border-radius: 20px !important;
  border: 1px solid rgba(255, 255, 255, 0.125);
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.1);
}

::v-deep .v-dialog {
  background-color: transparent !important;
  box-shadow: none !important;
}
</style>
